// import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query'
import { createApi,fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { JWT_TOKEN } from '../storagekeys'
import { cacheKeys } from './cacheKeys';
import { config } from './apiUrlConfig';
import { ENV_TYPES } from '../components/global/Sidebar';
import { authApiSlice } from './user/authentication/authApiSlice';
import { authMutex as mutex } from './authMutex';
import { refreshTokenAndRetry } from './authSync';

const baseQuery = fetchBaseQuery({
    baseUrl: config.url.API_ROOT_URL,
    prepareHeaders: (headers, { getState }) => {
        //console.log('[RootSlice:prepareHeaders] Preparing headers for request');
        const token = getState().auth.token
        const warehouseId = getState().warehouse.activeId;
        const type = getState().settings.environment.selectedEnvironmentType;
        const id = getState().settings.environment.selectedId;
        const relationRootId = getState().settings.environment.relationRootId;
        const relationCustomerId = getState().settings.environment.relationCustomerId;

        if (token) {
            const tokenStart = token.substring(0, 10);
            const tokenEnd = token.substring(token.length - 10);
            //console.log(`[RootSlice:prepareHeaders] Setting auth token: ${tokenStart}...${tokenEnd}`);
            headers.set('authorization', `Bearer ${token}`);
        } else {
            //console.warn('[RootSlice:prepareHeaders] No auth token available');
        }

        const language = getState().settings.language;
        if (language) {
            //console.log(`[RootSlice:prepareHeaders] Setting language: ${language}`);
            headers.set('Accept-Language', language);
        }

        if (warehouseId) {
           // console.log(`[RootSlice:prepareHeaders] Setting warehouse_id: ${warehouseId}`);
            headers.set('warehouse_id', warehouseId);
        }

        if(type === ENV_TYPES[100]){
            //CUSTOMER
           // console.log(`[RootSlice:prepareHeaders] Environment: CUSTOMER, id: ${id}`);
            headers.set("customer_id", id);
        } else if(type === ENV_TYPES[200]) {
            //RELATION
            //console.log(`[RootSlice:prepareHeaders] Environment: RELATION, id: ${id}, rootId: ${relationRootId}, customerId: ${relationCustomerId}`);
            headers.set("relation_id", id);
            headers.set("relation_root_id", relationRootId);
            headers.set("customer_id", relationCustomerId);
        } else {
            //console.log(`[RootSlice:prepareHeaders] Environment type: ${type}, id: ${id}`);
        }

        headers.set("Content-Type", 'application/json');
        //console.log('[RootSlice:prepareHeaders] Headers prepared successfully');

        return headers
    },
});

// Endpoints that should never trigger token refresh on 401
const noReauthEndpoints = ['login', 'refreshToken', 'requestPasswordReset', 'resetPassword', 'verifyResetCode'];

// Custom base query with re-authentication logic
const baseQueryWithReauth = async (args, api, extraOptions) => {
    const requestUrl = typeof args === 'string' ? args : args?.url;
    const endpointName = api.endpoint;
    // console.log(`[RootSlice:reauth] Starting request to: ${requestUrl}, endpoint: ${endpointName}`);
    // console.log(`[RootSlice:reauth] Mutex locked: ${mutex.isLocked()}`);

    // The refreshToken endpoint is dispatched by the reauth flow while the mutex is held,
    // so it must skip the wait — otherwise it deadlocks against its own caller.
    if (endpointName !== 'refreshToken') {
        await mutex.waitForUnlock();
        // console.log(`[RootSlice:reauth] Mutex unlocked, executing base query for: ${requestUrl}`);
    } else {
        // console.log(`[RootSlice:reauth] Skipping mutex wait for refreshToken endpoint`);
    }

    // The token this request is about to be sent with. refreshTokenAndRetry
    // compares it against the store, so a refresh that landed while we were
    // waiting for the response — ours or a sibling tab's — is adopted instead of
    // spending our rotated refresh token on a second one.
    const staleToken = api.getState().auth.token;

    let result = await baseQuery(args, api, extraOptions);
    // console.log(`[RootSlice:reauth] Base query result for ${requestUrl} - status: ${result.error?.status ?? 'OK'}, hasData: ${!!result.data}, hasError: ${!!result.error}`);

    if (result.error) {
        // console.warn(`[RootSlice:reauth] Request error for ${requestUrl}:`, JSON.stringify(result.error));
    }

    // Skip reauth for login endpoints so the 401 error bubbles up to the component
    if (result.error && result.error.status === 401 && noReauthEndpoints.includes(endpointName)) {
        // console.log(`[RootSlice:reauth] 401 received for ${endpointName} but skipping reauth (noReauthEndpoint)`);
        return result;
    }

    // refreshToken 401 returns directly — no recursive reauth attempt
    if (result.error && result.error.status === 401 && endpointName === 'refreshToken') {
        // console.log(`[RootSlice:reauth] 401 on refreshToken, returning error to caller`);
        return result;
    }

    if (result.error && result.error.status === 401) {
        // console.log(`[RootSlice:reauth] 401 received for ${requestUrl} (endpoint: ${endpointName}), attempting token refresh`);
        if (!mutex.isLocked()) {
            // console.log(`[RootSlice:reauth] Acquiring mutex for token refresh`);
            const release = await mutex.acquire();
            // console.log(`[RootSlice:reauth] Mutex acquired, starting token refresh`);
            try{
                // console.log(`[RootSlice:reauth] Dispatching refresh token request`);
                result = await refreshTokenAndRetry({
                    api,
                    staleToken,
                    refresh: (refreshToken) => api.dispatch(authApiSlice.endpoints.refreshToken.initiate({ "refresh_token": refreshToken })),
                    retry: () => baseQuery(args, api, extraOptions),
                    failedResult: result,
                });
                // console.log(`[RootSlice:reauth] Reauth result for ${requestUrl} - status: ${result.error?.status ?? 'OK'}, hasData: ${!!result.data}`);
            } finally {
                // release must be called once the mutex should be released again.
                // console.log(`[RootSlice:reauth] Releasing mutex`);
                release()
            }
        } else {
            // wait until the mutex is available without locking it
            // console.log(`[RootSlice:reauth] Mutex already locked, waiting for unlock before retrying ${requestUrl}`);
            await mutex.waitForUnlock()
            // console.log(`[RootSlice:reauth] Mutex unlocked, retrying request to: ${requestUrl}`);
            result = await baseQuery(args, api, extraOptions)
            // console.log(`[RootSlice:reauth] Retry after wait result for ${requestUrl} - status: ${result.error?.status ?? 'OK'}, hasData: ${!!result.data}`);
        }
    }

    // console.log(`[RootSlice:reauth] Returning result for ${requestUrl} - success: ${!result.error}`);
    return result;
};

export const rootSlice = createApi({
    reducerPath: "rootApi",
    baseQuery: baseQueryWithReauth,
    tagTypes: cacheKeys,
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({

    })
})

// export const {useGetRandQuery} = apiSlice